import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";

import { useAuth } from '../../contexts/AuthContext';

import { formatDate, formatHeure, formatCurrency, fileToBase64 } from "../../utils/Format";
import CustomSelect from "../../utils/CustomSelect";

import QuitusForm from './QuitusForm';

import useFetchDocs from '../../hooks/useFetchDocs';
import useFetchIntervenants from '../../hooks/useFetchIntervenants';

import config from '../../config/config';

const formatBytes = (bytes = 0) => {
    if (bytes === 0) return "0 o";
    const k = 1024;
    const sizes = ["o", "Ko", "Mo", "Go", "To"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const isZeroDate = (s) => !s || s === "0000-00-00";
const secondsToHHMM = (sec = 0) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};
const joinDateAndSeconds = (dateStr, seconds) => {
    if (isZeroDate(dateStr)) return null;
    const hhmm = secondsToHHMM(Number(seconds || 0));
    return `${dateStr}T${hhmm}:00`;
};

const base64Size = (b64 = "") => {
    const len = b64.length;
    const padding = (b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0);
    return Math.floor(len * 0.75) - padding;
};

const mapDocToAction = (doc, { declarationNom, intervenants }) => {
    const type = (doc._Type_Document || "").toLowerCase() === "expertise" ? "expertise" : "travaux";
    const titre = doc.Titre || "Document";
    const createdAt = joinDateAndSeconds(doc._Date_Crea, doc._Heure_Crea) || new Date().toISOString();
    const dateSouhaitee = isZeroDate(doc.Date_Convoc) ? null : doc.Date_Convoc;
    const heureSouhaitee = doc.Heure_Convoc ? formatHeure(doc.Heure_Convoc) : "";
    const rdvDate = isZeroDate(doc.Date_Rdv) ? null : doc.Date_Rdv;
    const rdvHeure = doc.Heure_Rdv ? formatHeure(doc.Heure_Rdv) : "";
    const intervenantUUID = doc.UUID_Intervenant || null;
    const intervenantNom = intervenantUUID
        ? (intervenants.find((i) => i.UUID_ === intervenantUUID)?.Nom || null)
        : null;
    const budgetEstime = typeof doc.Commande_Montant === "number" && doc.Commande_Montant > 0 ? doc.Commande_Montant : null;
    const createdBy = doc._Creer_Par || "";
    const caUser = doc.CA || "";
    const signatureBy = doc._Signature || "";
    const flags = {
        important: !!doc.Important,
        rar: !!doc.RAR,
        commande: !!doc.Commande,
        retourne: !!doc.Retourne,
        cloture: !!doc.Cloture,
        envoye: !!doc.Envoye,
        webPublier: !!doc.Web_Publier,
    };
    const downloadUrl = null;

    return {
        id: `doc-${doc.UUID || doc.Ref_Interne || titre}-${createdAt}`,
        type,
        titre,
        declarationNom,
        createdAt,
        dateSouhaitee,
        heureSouhaitee,
        rdvDate,
        rdvHeure,
        intervenantUUID,
        intervenantNom,
        budgetEstime,
        createdBy,
        caUser,
        signatureBy,
        flags,
        downloadUrl,
    };
};

const StepsHeader = ({ step }) => {
    const steps = [
        { id: 1, label: "Fichier" },
        { id: 2, label: "Infos" },
        { id: 3, label: "Résumé" },
    ];
    const percent = ((step - 1) / (steps.length - 1)) * 100;

    return (
        <div className="mb-6">
            <div className="relative bg-gray-200 rounded-full h-2">
                <div className="left-0 absolute inset-y-0 bg-orange-600 rounded-full transition-all" style={{ width: `${percent}%` }} />
            </div>
            <div className="grid grid-cols-3 mt-3 text-xs sm:text-sm">
                {steps.map((s, idx) => {
                    const active = step === s.id;
                    const done = step > s.id;
                    return (
                        <div key={s.id} className="flex flex-col items-center gap-1">
                            <div
                                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center 
                ${done ? "bg-orange-600 border-orange-600 text-white" : active ? "border-orange-600 text-orange-600" : "border-gray-300 text-gray-500"}`}
                            >
                                {s.id}
                            </div>
                            <div className={`${active ? "text-orange-700 font-medium" : "text-gray-600"}`}>{s.label}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ActionsTab = ({ declaration, intervenants = [], onCreate }) => {
    const { hasRole } = useAuth();

    const { lstDocs, fetchDocsByDeclaUUID, createDoc, loading: docsLoading } = useFetchDocs();

    const { contacts, fetchContactsIntervenants, fetchContactsIntervenantsBySiteUUID, fetchAddIntervenantToDeclaration } = useFetchIntervenants();

    const [listIntervenantsSelected, setListIntervenantsSelected] = useState([]);
    const [selectedContactId, setSelectedContactId] = useState("");
    const [selectedContact, setSelectedContact] = useState(null);
    const [showAllContacts, setShowAllContacts] = useState(false);

    const quitusRef = useRef(null);
    const [quitusPdfB64, setQuitusPdfB64] = useState(null);
    const [quitusPdfSize, setQuitusPdfSize] = useState(0);

    // const intervenantUUIDs = useMemo(
    //     () => (Array.isArray(intervenants) ? intervenants.map(i => i.UUID_Intervenant).filter(Boolean) : []),
    //     [intervenants]
    // );

    console.log("declaration changed:", declaration);

    useEffect(() => {
        if (showAllContacts) {
            fetchContactsIntervenants();
        } else if (declaration?.UUID_Site_) {
            fetchContactsIntervenantsBySiteUUID(declaration?.UUID_Site_);
        }
    }, [declaration?.UUID_Site_, showAllContacts]);

    useEffect(() => {
        if (declaration?.UUID_) fetchDocsByDeclaUUID(declaration.UUID_);
    }, [declaration?.UUID_]);

    useEffect(() => {
        if (!Array.isArray(contacts) || contacts.length === 0) {
            setListIntervenantsSelected([]);
            return;
        }

        const groups = new Map();

        const contactsWithEmail = contacts.filter(c => c.Email && c.Email.trim() !== "");

        contactsWithEmail.forEach((c) => {
            const parentId = c.UUID_Intervenant_ || null;
            if (!groups.has(parentId)) {
                groups.set(parentId, {
                    id: parentId,
                    rawName: c.Intervenant_Nom || "Intervenant",
                    type: c.Intervenant_Type || "",
                    subOptions: []
                });
            }
            groups.get(parentId).subOptions.push({
                id: c.UUID_,
                label: `${c.Nom}${` <${c.Email}>`}`,
                selected: selectedContactId === c.UUID_,
                __raw: c,
            });
        });

        const formatted = Array.from(groups.values()).map((g, idx) => ({
            id: idx + 1,
            label: g.type ? `${g.rawName} (${g.type})` : g.rawName,
            subOptions: g.subOptions,
        }));
        console.log("groups:", groups);
        console.log("Formatted contacts for CustomSelect:", formatted);
        setListIntervenantsSelected(formatted);
    }, [contacts, selectedContactId]);

    const handleRelIntSelectionChange = (contactUUID) => {
        setSelectedContactId(prev => (prev === contactUUID ? "" : contactUUID));

        let found = null;
        (contacts || []).some(c => {
            if (c.UUID_ === contactUUID) { found = c; return true; }
            return false;
        });

        if (found) {
            setSelectedContact(found);
            setIntervenantUUID(found.UUID_Intervenant_ || "");
            setIntervenantNom(`${found.Intervenant} — ${found.Nom}`);
        } else {
            setSelectedContact(null);
            setIntervenantUUID("");
            setIntervenantNom("");
        }

        setListIntervenantsSelected(prev =>
            prev.map(group => ({
                ...group,
                subOptions: group.subOptions.map(so => ({
                    ...so,
                    selected: so.id === contactUUID ? !(so.selected) : false // sélection unique
                }))
            }))
        );
    };

    const [step, setStep] = useState(1);

    const [type, setType] = useState("");
    const [titre, setTitre] = useState("");
    const [delaiJours, setDelaiJours] = useState('');
    const [dateSouhaitee, setDateSouhaitee] = useState(null);
    const [heureSouhaitee, setHeureSouhaitee] = useState("");
    const [commentaire, setCommentaire] = useState("");
    const [budget, setBudget] = useState("");
    const [pieceJointe, setPieceJointe] = useState(null);

    const [intervenantNom, setIntervenantNom] = useState("");
    const [intervenantUUID, setIntervenantUUID] = useState("");

    const [dragActive, setDragActive] = useState(false);
    const [recentActions, setRecentActions] = useState([]);
    const [successMessage, setSuccessMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const calcDateOuvrable = (jours) => {
        let d = new Date();
        let count = 0;
        while (count < jours) {
            d.setDate(d.getDate() + 1);
            const day = d.getDay();
            if (day !== 0 && day !== 6) { // 0 = dimanche, 6 = samedi
                count++;
            }
        }
        return d;
    };

    const intervenantOptions = useMemo(() => intervenants.map((i) => i.Nom), [intervenants]);

    const validateStep1 = async () => {
        const e = {};
        if (type === "quitus") {
            if (!quitusRef.current?.isValid()) {
                e.quitus = "Veuillez compléter les champs obligatoires du quitus (entreprise, client, date de réception).";
            }
        } else {
            if (!titre.trim() && type !== "expertise") e.titre = "Titre requis";
            if (type === "expertise" && !pieceJointe) e.file = "Fichier requis";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const validateStep2 = () => {
        const e = {};
        if (type === "travaux") {
            if (!titre.trim()) e.titre = "Titre requis";
            if (!dateSouhaitee) e.dateSouhaitee = "Date souhaitée requise";
        } else if (type === "expertise") {
            if (!dateSouhaitee) e.dateSouhaitee = "Date d'expertise requise";
            if (!heureSouhaitee) e.heureSouhaitee = "Heure d'expertise requise";
        } else if (type === "quitus") {
            if (!titre.trim()) e.titre = "Titre requis";
            if (!quitusPdfB64) e.file = "PDF Quitus manquant (revenir à l’étape 1).";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const resetForm = () => {
        setStep(1);
        setTitre("");
        setDateSouhaitee("");
        setCommentaire("");
        setBudget("");
        setPieceJointe(null);
        setIntervenantNom("");
        setIntervenantUUID("");
        setErrors({});
        setDragActive(false);
    };

    const nextStep = async () => {
        if (step === 1) {
            const ok = await validateStep1();
            if (!ok) return;

            if (type === "quitus") {
                const b64 = await quitusRef.current.getPdfBase64();
                const baseTitle = quitusRef.current.getTitle();
                setQuitusPdfB64(b64);
                setQuitusPdfSize(base64Size(b64));
                setPieceJointe({
                    name: `${baseTitle}.pdf`,
                    size: base64Size(b64),
                    type: "application/pdf",
                });
                setTitre((t) => t || baseTitle);
            }
        }

        if (step === 2 && !validateStep2()) return;
        setStep((s) => Math.min(3, s + 1));
    };

    const prevStep = () => setStep((s) => Math.max(1, s - 1));

    // submit (étape 3)
    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (!validateStep2()) return;
        setSubmitting(true);
        try {
            let filePart = null;

            if (type === "quitus") {
                // on a déjà le base64 + meta
                filePart = {
                    base64: quitusPdfB64,
                    size: quitusPdfSize,
                    mime: "application/pdf",
                    name: pieceJointe?.name || `${quitusRef.current?.getTitle() || "Quitus"}.pdf`,
                };
            } else {
                if (pieceJointe) filePart = await fileToBase64(pieceJointe);
            }

            const titreFinal =
                type === "expertise"
                    ? (titre?.trim() || pieceJointe?.name || "Demande d’expertise")
                    : (type === "quitus"
                        ? (titre?.trim() || "Quitus de réception")
                        : titre);

            const mimeToExt = {
                "application/pdf": "pdf",
                "image/jpeg": "jpg",
                "image/png": "png",
                "image/gif": "gif",
                "application/msword": "doc",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
                "application/vnd.ms-excel": "xls",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
                "text/plain": "txt",
                "text/csv": "csv",
            };

            let fileExtension = "";
            if (type === "quitus") {
                fileExtension = "pdf";
            } else if (pieceJointe) {
                if (pieceJointe.name && pieceJointe.name.includes(".")) {
                    fileExtension = pieceJointe.name.split(".").pop().toLowerCase();
                } else if (pieceJointe.type && mimeToExt[pieceJointe.type]) {
                    fileExtension = mimeToExt[pieceJointe.type];
                }
            }

            const payload = {
                baseReactUrl: config.reactBaseUrl,
                _Type_Document: type === "expertise" ? "Expertise" : (type === "quitus" ? "Quitus" : "Travaux"),
                Titre: titreFinal,
                Resume: commentaire || "",
                UUID_Declaration: declaration?.UUID_ || "",
                UUID_Site: declaration?.UUID_Site_ || "",
                Date_Convoc: (dateSouhaitee || ""),
                UUID_Intervenant: type === "travaux" ? (intervenantUUID || "") : "",
                Contact: selectedContact,
                File_Name: filePart?.name || "",
                File_Mime: filePart?.mime || "",
                File_Type: fileExtension,
                File_Size: filePart?.size || 0,
                File_Base64: filePart?.base64 || "",
            };

            await createDoc(payload);
            if (type === "travaux" && intervenantUUID) {
                await fetchAddIntervenantToDeclaration(intervenantUUID, declaration?.UUID_);
            }

            if (declaration?.UUID_) await fetchDocsByDeclaUUID(declaration.UUID_);

            setRecentActions((prev) => [
                {
                    id: `local-${Date.now()}`,
                    type,
                    titre: titreFinal,
                    dateSouhaitee,
                    intervenantUUID,
                    intervenantNom: intervenants.find(i => i.UUID_ === intervenantUUID)?.Nom || intervenantNom || null,
                    commentaire,
                    declarationNom: declaration?.Nom,
                    createdAt: new Date().toISOString(),
                },
                ...prev.slice(0, 9),
            ]);

            setSuccessMessage("L’action a bien été créée avec succès !");
            setTimeout(() => setSuccessMessage(""), 5000);
            resetForm();
            // reset quitus state
            setQuitusPdfB64(null);
            setQuitusPdfSize(0);
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };


    const actionsFromDocs = useMemo(() => {
        if (!Array.isArray(lstDocs)) return [];
        return lstDocs.map((doc) => mapDocToAction(doc, { declarationNom: declaration?.Nom, intervenants }));
    }, [lstDocs, declaration?.Nom, intervenants]);

    const allActions = useMemo(() => {
        const list = [...actionsFromDocs, ...recentActions];
        return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [actionsFromDocs, recentActions]);

    const handleFileSelect = (file) => {
        if (!file) return;
        setPieceJointe(file);
        if (type === "expertise" && !titre) setTitre(file.name.replace(/\.[^/.]+$/, ""));
    };
    const onInputChange = (e) => handleFileSelect(e.target.files?.[0] ?? null);
    const onDragOver = useCallback((e) => { e.preventDefault(); setDragActive(true); }, []);
    const onDragLeave = useCallback((e) => { e.preventDefault(); setDragActive(false); }, []);
    const onDrop = useCallback((e) => { e.preventDefault(); setDragActive(false); handleFileSelect(e.dataTransfer?.files?.[0]); }, []);
    const handleIntervenantChange = (nomChoisi) => {
        setIntervenantNom(nomChoisi);
        const found = intervenants.find((i) => i.Nom === nomChoisi);
        setIntervenantUUID(found?.UUID_ || "");
    };

    // Boutons navigation
    const NavButtons = ({ onSubmit }) => (
        <div className="flex justify-between items-center col-span-full">
            {step > 0 && (
                <button
                    type="button"
                    onClick={prevStep}
                    disabled={step === 1 || submitting}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border transition
          ${step === 1 ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-gray-300 text-gray-700 hover:border-orange-400"}`}
                >
                    <i className="fa-arrow-left fa-solid" /> Précédent
                </button>
            )}

            {step < 3 ? (
                <button
                    type="button"
                    onClick={async () => { await nextStep(); }}
                    className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 px-4 py-2 rounded-lg text-white transition"
                >
                    Suivant <i className="fa-arrow-right fa-solid" />
                </button>
            ) : (
                <button
                    type="button"
                    onClick={onSubmit}
                    disabled={submitting}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white transition
            ${submitting ? "bg-orange-300 cursor-not-allowed" : "bg-orange-600 hover:bg-orange-500"}`}
                >
                    {submitting ? "Création..." : "Créer"}
                    <i className="fa-solid fa-check" />
                </button>
            )}
        </div>
    );

    const ALL_TABS = [
        { id: "expertise", label: "Demande d’expertise", icon: "fa-magnifying-glass", exclude: ["visiteur"] },
        { id: "travaux", label: "Demande de travaux", icon: "fa-wrench", exclude: ["visiteur"] },
        { id: "quitus", label: "Quitus de réception", icon: "fa-file-signature" },
    ];

    const canSee = (tab) => {
        if (tab.only) return tab.only.some((r) => hasRole(r));
        if (tab.exclude) return !tab.exclude.some((r) => hasRole(r));
        return true;
    };

    const visibleTabs = ALL_TABS.filter(canSee);

    const safeActiveTab = visibleTabs.some(t => t.id === setType)
        ? setType
        : (visibleTabs[0]?.id ?? null);

    return (
        <div className="mx-auto">
            <div className="bg-white shadow-lg mb-6 p-4 md:p-8 rounded-lg">
                <div className="flex md:flex-row flex-col md:items-center gap-4">
                    <div className="flex items-center gap-3">
                        <div className="flex justify-center items-center bg-orange-600 rounded-lg w-10 h-10 text-white">
                            <i className="fa-solid fa-clipboard-list" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-800 text-xl">Créer des Actions</h3>
                            <p className="text-gray-500 text-sm">Créez et suivez vos demandes (travaux / expertise / quitus)</p>
                        </div>
                    </div>

                    <div className="flex gap-2 md:ml-auto">
                        {visibleTabs.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => { setType(t.id); setStep(1); setErrors({}); }}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition ${type === t.id ? "bg-orange-600 border-orange-600 text-white" : "bg-white border-gray-300 text-gray-700 hover:border-orange-400"}`}
                            >
                                <i className={`fa-solid ${t.icon}`} />
                                <span>{t.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {successMessage && (
                <div className="mb-4">
                    <div className="flex items-center gap-2 bg-emerald-100 shadow-sm px-4 py-2 border border-emerald-300 rounded-lg text-emerald-800">
                        <i className="fa-solid fa-circle-check"></i>
                        <span>{successMessage}</span>
                    </div>
                </div>
            )}


            {!type ? (
                // <div className="py-8 text-center">
                //     <p className="mb-6 text-gray-600">
                //         Choisissez le type d’action à créer :
                //     </p>
                //     <div className="flex flex-wrap justify-center gap-2">
                //         {[
                //             { id: "expertise", label: "Demande d’expertise", icon: "fa-magnifying-glass" },
                //             { id: "travaux", label: "Demande de travaux", icon: "fa-wrench" },
                //             { id: "quitus", label: "Quitus de réception", icon: "fa-file-signature" },
                //         ].map((t) => (
                //             <button
                //                 key={t.id}
                //                 type="button"
                //                 onClick={() => { setType(t.id); setStep(1); setErrors({}); }}
                //                 className="flex items-center gap-2 bg-white px-4 py-2 border border-gray-300 hover:border-orange-400 rounded-lg text-gray-700 text-sm transition"
                //             >
                //                 <i className={`fa-solid ${t.icon}`} />
                //                 <span>{t.label}</span>
                //             </button>
                //         ))}
                //     </div>
                //     {errors?.type && <p className="mt-4 text-red-500 text-sm">{errors.type}</p>}
                // </div>
                <></>
            ) : (
                <div className="bg-white shadow-lg mb-6 p-4 md:p-8 rounded-lg overflow-hidden">
                    <StepsHeader step={step} />
                    <div className="relative w-full overflow-hidden">
                        <div
                            className="flex transition-transform duration-500 ease-in-out"
                            style={{ transform: `translateX(-${(step - 1) * (33.3334)}%)`, width: "300%" }}
                        >
                            {/* --- STEP 1: FICHIER --- */}
                            <div className="pr-0 md:pr-6 w-full" style={{ width: "100%" }}>
                                <div className="gap-6 grid grid-cols-1 lg:grid-cols-12">


                                    {type !== "quitus" ? (
                                        <div className="space-y-4 lg:col-span-7">
                                            <div>
                                                <label className="block font-medium text-gray-700 text-sm">
                                                    {type === "expertise" ? "Déposez votre convocation d’expertise" : "Déposez votre demande de travaux"}
                                                    <span className="text-red-500"> *</span>
                                                </label>
                                                <div
                                                    onDragOver={onDragOver}
                                                    onDragLeave={onDragLeave}
                                                    onDrop={onDrop}
                                                    className={`mt-1 w-full border-2 border-dashed rounded-xl p-6 transition h-full
                                            ${dragActive ? "border-orange-500 bg-orange-50" : "border-gray-300 bg-gray-50 hover:border-orange-400"}`}
                                                >
                                                    <div className="flex flex-col justify-center items-center gap-3 text-center">
                                                        <div className="flex justify-center items-center bg-orange-600 rounded-lg w-12 h-12 text-white">
                                                            <i className="fa-solid fa-file-arrow-up" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <p className="font-medium text-gray-800">Glissez-déposez un fichier ici</p>
                                                            <p className="text-gray-500 text-sm">
                                                                ou
                                                                <label className="ml-1 font-medium text-orange-700 underline cursor-pointer">
                                                                    choisissez un fichier
                                                                    <input type="file" onChange={onInputChange} className="hidden" />
                                                                </label>
                                                            </p>
                                                        </div>
                                                        <p className="opacity-0 text-red-500 text-xs">Fichier</p>
                                                        {pieceJointe && (
                                                            <div className="bg-white mt-2 px-3 py-2 border border-gray-200 rounded-lg w-full max-w-md text-left">
                                                                <div className="flex justify-between items-center gap-3">
                                                                    <div className="min-w-0">
                                                                        <p className="font-medium text-gray-800 text-sm truncate">{pieceJointe.name}</p>
                                                                        <p className="text-gray-500 text-xs">{formatBytes(pieceJointe.size)}</p>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setPieceJointe(null)}
                                                                        className="text-red-600 hover:text-red-700 text-sm"
                                                                    >
                                                                        Retirer
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-4 lg:col-span-12">
                                            <NavButtons onSubmit={handleSubmit} />
                                            {errors.quitus && <p className="mt-1 text-red-500 text-xs">{errors.quitus}</p>}
                                            <QuitusForm ref={quitusRef} declaration={declaration} />
                                        </div>
                                    )}
                                    {type !== "quitus" && (
                                        <>
                                            <div className="space-y-4 lg:col-span-5">
                                                {/* Titre */}
                                                <div>
                                                    <label className="block flex font-medium text-gray-700 text-sm">
                                                        Titre {type === "travaux" && <span className="text-red-500">*</span>}{errors.titre && <p className="mt-0.5 ml-2 text-red-500 text-xs">({errors.titre})</p>}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={titre}
                                                        onChange={(e) => setTitre(e.target.value)}
                                                        placeholder="Titre..."
                                                        className={`mt-1 px-3 py-2 border rounded-lg w-full ${errors.titre ? "border-red-400" : "border-gray-300"}`}
                                                    />

                                                </div>
                                                <div>
                                                    <label className="block font-medium text-gray-700 text-sm">Commentaire (optionnel)</label>
                                                    <textarea
                                                        value={commentaire}
                                                        onChange={(e) => setCommentaire(e.target.value)}
                                                        placeholder="Informations additionnelles si besoin"
                                                        className="mt-1 px-3 py-2 border border-gray-300 rounded-lg w-full min-h-[90px]"
                                                    />
                                                </div>


                                            </div>
                                            <NavButtons onSubmit={handleSubmit} />
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* --- STEP 2: INFOS --- */}
                            <div className="px-0 md:px-6 w-full" style={{ width: "100%" }}>
                                <div className="gap-6 grid grid-cols-1 lg:grid-cols-12">
                                    <div className="space-y-4 lg:col-span-6">

                                        {type === "travaux" ? (
                                            <>
                                                <label className="block font-medium text-gray-700 text-sm">
                                                    Délai (en jours ouvrés) <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={delaiJours}
                                                    onChange={(e) => {
                                                        const v = parseInt(e.target.value, 10) || 0;
                                                        setDelaiJours(v);
                                                        setDateSouhaitee(calcDateOuvrable(v));
                                                    }}
                                                    placeholder="Délai en jours ouvrés"
                                                    className={`mt-1 px-3 py-2 border rounded-lg w-full ${errors.dateSouhaitee ? "border-red-400" : "border-gray-300"
                                                        }`}
                                                />
                                                {dateSouhaitee && (
                                                    <p className="mt-2 text-gray-600 text-sm">
                                                        Date estimée de réalisation :{" "}
                                                        <span className="font-semibold text-orange-600">
                                                            {new Date(dateSouhaitee).toLocaleDateString("fr-FR", {
                                                                weekday: "long",
                                                                year: "numeric",
                                                                month: "long",
                                                                day: "numeric",
                                                            })}
                                                        </span>
                                                    </p>
                                                )}
                                                {errors.dateSouhaitee && (
                                                    <p className="mt-1 text-red-500 text-xs">{errors.dateSouhaitee}</p>
                                                )}
                                            </>
                                        ) : type === "expertise" ? (
                                            <div className="space-y-3">
                                                {/* UI expertise identique à aujourd'hui (date + heure) */}
                                                <div>
                                                    <label className="block font-medium text-gray-700 text-sm">
                                                        Date d'expertise <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={dateSouhaitee}
                                                        onChange={(e) => setDateSouhaitee(e.target.value)}
                                                        className={`mt-1 px-3 py-2 border rounded-lg w-full ${errors.dateSouhaitee ? "border-red-400" : "border-gray-300"}`}
                                                    />
                                                    {errors.dateSouhaitee && <p className="mt-1 text-red-500 text-xs">{errors.dateSouhaitee}</p>}
                                                </div>
                                                <div>
                                                    <label className="block font-medium text-gray-700 text-sm">
                                                        Heure d'expertise <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="time"
                                                        value={heureSouhaitee}
                                                        onChange={(e) => setHeureSouhaitee(e.target.value)}
                                                        className={`mt-1 px-3 py-2 border rounded-lg w-full ${errors.heureSouhaitee ? "border-red-400" : "border-gray-300"}`}
                                                    />
                                                    {errors.heureSouhaitee && <p className="mt-1 text-red-500 text-xs">{errors.heureSouhaitee}</p>}
                                                </div>
                                            </div>
                                        ) : (
                                            // === QUITUS ===
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block flex font-medium text-gray-700 text-sm">
                                                        Titre <span className="ml-1 text-red-500">*</span>
                                                        {errors.titre && <span className="ml-2 text-red-500 text-xs">({errors.titre})</span>}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={titre}
                                                        onChange={(e) => setTitre(e.target.value)}
                                                        placeholder="ex: Quitus réception appartement B12"
                                                        className={`mt-1 px-3 py-2 border rounded-lg w-full ${errors.titre ? "border-red-400" : "border-gray-300"}`}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block font-medium text-gray-700 text-sm">Commentaire (optionnel)</label>
                                                    <textarea
                                                        value={commentaire}
                                                        onChange={(e) => setCommentaire(e.target.value)}
                                                        placeholder="Informations additionnelles si besoin"
                                                        className="mt-1 px-3 py-2 border border-gray-300 rounded-lg w-full min-h-[90px]"
                                                    />
                                                </div>
                                                {errors.file && <p className="text-red-500 text-xs">{errors.file}</p>}
                                            </div>
                                        )}


                                    </div>
                                    <div className="space-y-4 lg:col-span-6">


                                        {/* Intervenant (travaux uniquement) */}
                                        {type === "travaux" ? (

                                            <div className="flex justify-between items-center mt-2.5">
                                                <CustomSelect
                                                    options={listIntervenantsSelected}
                                                    label="Intervenant / Contact assigné"
                                                    value={
                                                        selectedContact
                                                            ? `${selectedContact.Intervenant_Nom} / ${selectedContact.Nom}${selectedContact.Email ? ` <${selectedContact.Email}>` : ""}`
                                                            : "— Aucun contact sélectionné —"
                                                    }
                                                    onChange={handleRelIntSelectionChange}
                                                    multiple={false}
                                                    isNested={true}
                                                />
                                                <label class="flex gap-2 mt-5 min-w-36 mcui-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={showAllContacts}
                                                        onChange={() => setShowAllContacts(!showAllContacts)}
                                                    />
                                                    <div>
                                                        <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                                            <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                                        </svg>
                                                    </div>
                                                    <span className="text-gray-600 text-lg">Afficher Tous</span>
                                                </label>
                                            </div>
                                        ) : type === "expertise" ? (
                                            <CustomSelect
                                                options={[]}
                                                label="CA assigné"
                                                value={"— Aucun CA sélectionné —"}
                                                onChange={handleRelIntSelectionChange}
                                                multiple={false}
                                            />
                                        ) : (
                                            <></>
                                        )}


                                    </div>
                                    <NavButtons onSubmit={handleSubmit} />
                                </div>
                            </div>

                            {/* --- STEP 3: RÉSUMÉ --- */}
                            <div className="pl-0 md:pl-6 w-full" style={{ width: "100%" }}>
                                <div className="gap-6 grid grid-cols-1 lg:grid-cols-12">
                                    <div className="space-y-4 lg:col-span-7">
                                        <div className="p-4 border rounded-lg">
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className="flex justify-center items-center bg-orange-600 rounded-md w-8 h-8 text-white">
                                                    <i className={`fa-solid ${type === "travaux" ? "fa-wrench" : "fa-magnifying-glass"}`} />
                                                </div>
                                                <div className="font-semibold text-gray-800">
                                                    {type === "expertise"
                                                        ? "Résumé de la demande d'expertise"
                                                        : type === "travaux"
                                                            ? "Résumé de la demande de Travaux"
                                                            : "Résumé du Quitus"}
                                                </div>
                                            </div>

                                            <ul className="space-y-1 text-gray-700 text-sm">
                                                <li><b>Titre :</b> {type === "expertise" ? (titre || "(sera repris du nom du fichier)") : (titre || "—")}</li>
                                                <li><b>Type :</b> {type}</li>
                                                <li><b>Date souhaitée :</b> {dateSouhaitee ? formatDate(dateSouhaitee) : "—"}</li>
                                                {type === "travaux" && (
                                                    <li><b>Intervenant / Contact :</b> {selectedContact
                                                        ? `${selectedContact.Intervenant_Nom} / ${selectedContact.Nom}${selectedContact.Email ? ` <${selectedContact.Email}>` : ""}`
                                                        : "— Aucun contact sélectionné —"}</li>
                                                )}
                                                <li className="pt-1"><b>Commentaire :</b><br />{commentaire || "—"}</li>
                                            </ul>
                                        </div>

                                        <div className="p-4 border rounded-lg">
                                            <div className="mb-2 font-semibold text-gray-800">Fichier</div>
                                            {pieceJointe ? (
                                                <div className="flex justify-between items-center text-sm">
                                                    <div className="truncate">
                                                        <div className="font-medium">{pieceJointe.name}</div>
                                                        <div className="text-gray-500">{formatBytes(pieceJointe.size)}</div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setPieceJointe(null); setStep(1); }}
                                                        className="text-red-600 hover:text-red-700"
                                                    >
                                                        Retirer
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="text-gray-500 text-sm">Aucun fichier sélectionné.</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-4 lg:col-span-5">
                                        <div className="bg-orange-50 p-4 border border-orange-200 rounded-lg">
                                            <div className="mb-1 font-semibold text-orange-700">Vérifiez avant création</div>
                                            <p className="text-orange-700/80 text-sm">
                                                Les informations ci‑dessus seront envoyées dans 4D pour créer le document
                                                {type === "expertise" ? " d’expertise." : " de travaux."}
                                            </p>
                                        </div>
                                        <NavButtons onSubmit={handleSubmit} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Actions récentes */}
            <div className="bg-white shadow-lg mb-6 p-4 md:p-6 rounded-lg">
                <div className="flex items-center gap-2 mb-4">
                    <i className="text-orange-600 fa-clock-rotate-left fa-solid" />
                    <h4 className="font-semibold text-gray-800">Actions récentes</h4>
                </div>

                {allActions.length === 0 ? (
                    <p className="text-gray-500 text-sm">Aucune action créée pour le moment.</p>
                ) : (
                    <ul className="divide-y divide-gray-200">
                        {allActions.map((a) => (
                            <li key={a.id} className="flex gap-3 py-4">
                                <div className="flex justify-center items-center bg-gray-100 rounded-md w-10 h-10 shrink-0">
                                    <i className={`fa-solid ${a.type === "travaux" ? "fa-wrench" : "fa-magnifying-glass"} text-gray-700`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold text-gray-900 truncate">{a.titre}</span>
                                        <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-700 text-xs">{a.type}</span>
                                        {a.flags?.important && <span className="bg-red-100 px-2 py-0.5 rounded text-red-700 text-xs">Important</span>}
                                        {a.flags?.rar && <span className="bg-amber-100 px-2 py-0.5 rounded text-amber-700 text-xs">RAR</span>}
                                        {a.flags?.commande && <span className="bg-emerald-100 px-2 py-0.5 rounded text-emerald-700 text-xs">Commandé</span>}
                                        {a.flags?.cloture && <span className="bg-slate-200 px-2 py-0.5 rounded text-slate-700 text-xs">Clôturé</span>}
                                        {a.flags?.envoye && <span className="bg-blue-100 px-2 py-0.5 rounded text-blue-700 text-xs">Envoyé</span>}
                                        {a.flags?.retourne && <span className="bg-violet-100 px-2 py-0.5 rounded text-violet-700 text-xs">Retourné</span>}
                                        {a.flags?.webPublier && <span className="bg-orange-100 px-2 py-0.5 rounded text-orange-700 text-xs">Publié web</span>}
                                        {a.downloadUrl && (
                                            <a href={a.downloadUrl} target="_blank" rel="noreferrer" className="bg-orange-100 px-2 py-0.5 rounded text-orange-700 text-xs">
                                                Télécharger
                                            </a>
                                        )}
                                    </div>
                                    <div className="space-x-2 mt-1 text-gray-600 text-sm">
                                        {a.declarationNom && <span>Déclaration: <b>{a.declarationNom}</b></span>}
                                        {a.dateSouhaitee ? (
                                            <span>Convocation: <b>{formatDate(a.dateSouhaitee)}</b>{a.heureSouhaitee ? <b> à {a.heureSouhaitee}</b> : null}</span>
                                        ) : (
                                            <span>Créée le: <b>{formatDate(a.createdAt)}</b></span>
                                        )}
                                        {a.rdvDate && (
                                            <>
                                                <span>RDV: <b>{formatDate(a.rdvDate)}</b>{a.rdvHeure ? <b> à {a.rdvHeure}</b> : null}</span>
                                            </>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-gray-600 text-sm">
                                        {a.caUser && <span>CA: <b>{a.caUser}</b></span>}
                                        {a.createdBy && <span>Créé par: <b>{a.createdBy}</b></span>}
                                        {a.signatureBy && <span>Signé par: <b>{a.signatureBy}</b></span>}
                                        {a.intervenantNom && <span>Intervenant: <b>{a.intervenantNom}</b></span>}
                                        {typeof a.budgetEstime === "number" && <span>Budget: <b>{formatCurrency(a.budgetEstime)}</b></span>}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default ActionsTab;
